import { useMemo } from "react";
import { Alert, Card } from "react-bootstrap";

import * as api from "../../api/api";
import { useDroneStateRtdbDocuments } from "../../api/rtdbWrappers.generated.jsx";
import { useUser } from "../../contexts/user_provider";
import { useDronesOnline } from "../../hooks/useDroneOnline.jsx";
import urls from "../../urls.jsx";
import { CorvusList } from "../CorvusList";
import DroneCard from "../DroneCard";

const STATE_ORDER = ["Collecting", "Uploading", "Idle", "Offline"];

function getUploadBytes(uploadStatus = "(0.00 B)") {
    const match = uploadStatus.match(/\(([\d.]+)\s/);
    return match ? Number(match[1]) : 0;
}

function getBattery(state) {
    return state?.widgets?.battery?.data ?? -1;
}

function getDroneState(state, online) {
    const uploadStatus = state?.widgets?.payloadUploadStatus?.data;
    const uploading = getUploadBytes(uploadStatus);
    const collecting = (state?.missions ?? []).some((mission) => {
        // Only use missions which are less than 2 days old
        const timestamp = new Date(mission.timestamp).getTime();
        return Date.now() - timestamp <= 2 * 24 * 60 * 60 * 1000;
    });

    if (collecting) {
        return {
            state: "Collecting",
            stateColor: "text-primary",
        };
    }

    if (uploading > 0 && online) {
        return {
            state: "Uploading",
            stateColor: "text-primary",
        };
    }

    if (online) {
        return {
            state: "Idle",
            stateColor: "text-success",
        };
    }

    return {
        state: "Offline",
        stateColor: "text-secondary",
    };
}

function sortDrones(a, b) {
    if (a.hasLeftOrganization !== b.hasLeftOrganization) {
        return a.hasLeftOrganization ? 1 : -1;
    }

    if (a.state !== b.state) {
        return STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
    }

    if (a.state === "Offline") {
        return a.droneId.localeCompare(b.droneId);
    }

    return getBattery(b.droneState) - getBattery(a.droneState);
}

export default function DroneCardList({
    droneIds = [],
    organizationId,
    locationId,
    showSlider = false,
    className,
    title = "Drones",
}) {
    const { getUserIsAdmin } = useUser();
    const [droneConfigs, isLoadingDroneConfigs, droneConfigsError] =
        api.useDroneConfigsFromOrganization(organizationId, { type: "map" });
    const hasResolvedDroneConfigs =
        !isLoadingDroneConfigs && !droneConfigsError;
    const requestedDroneIds = useMemo(
        () =>
            [...new Set(droneIds ?? [])].filter(
                (droneId) => droneId !== undefined && droneId !== null,
            ),
        [droneIds],
    );
    const currentOrganizationDroneIds = useMemo(() => {
        if (!hasResolvedDroneConfigs) {
            return [];
        }

        return requestedDroneIds.filter((droneId) => droneConfigs[droneId]);
    }, [requestedDroneIds, droneConfigs, hasResolvedDroneConfigs]);
    const [droneStates] = useDroneStateRtdbDocuments(
        currentOrganizationDroneIds,
        {
            type: "map",
        },
    );
    const onlineStatuses = useDronesOnline(currentOrganizationDroneIds);

    const drones = requestedDroneIds
        .map((droneId) => {
            const droneConfig = hasResolvedDroneConfigs
                ? droneConfigs[droneId]
                : undefined;
            const droneState = droneStates[droneId];
            const hasLeftOrganization = hasResolvedDroneConfigs && !droneConfig;
            const online = onlineStatuses[droneId] ?? false;
            const { state, stateColor } = getDroneState(droneState, online);

            return {
                droneId,
                droneConfig,
                droneState,
                hasLeftOrganization,
                state: hasLeftOrganization ? "Left the organization" : state,
                stateColor: hasLeftOrganization ? "text-secondary" : stateColor,
                online: hasLeftOrganization ? false : online,
            };
        })
        .sort(sortDrones);

    return (
        <Card className={className}>
            <Card.Header className="d-flex justify-content-between">
                <div style={{ fontWeight: "bold" }}>{title}</div>
            </Card.Header>

            {drones.length === 0 ? (
                <Card.Body>
                    <Alert className="text-center mb-0" variant="primary">
                        <div className="text-muted">No Drones</div>
                    </Alert>
                </Card.Body>
            ) : (
                <CorvusList height={drones.length > 5 ? "411px" : "100%"}>
                    {drones.map((drone) => (
                        <DroneCard
                            key={drone.droneId}
                            state={drone.state}
                            stateColor={drone.stateColor}
                            offline={!drone.online}
                            name={drone.droneId}
                            droneState={drone.droneState}
                            locationId={locationId}
                            organizationId={organizationId}
                            to={
                                drone.droneConfig ||
                                (drone.hasLeftOrganization && getUserIsAdmin())
                                    ? urls.locationDrone(
                                          organizationId,
                                          drone.droneConfig?.locationId ??
                                              locationId,
                                          drone.droneId,
                                      )
                                    : undefined
                            }
                            showSlider={
                                Boolean(drone.droneConfig) && showSlider
                            }
                        />
                    ))}
                </CorvusList>
            )}
        </Card>
    );
}
